import React, { useCallback, useEffect, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import PageWrapper from '../../helper/PageWrapper'
import StaigeButton from '../../components/styles/Button'
import i18n from '../../languages/i18n'
import AiswSpinner from '../helper/aiswSpinner/aiswSpinner'
import { CameraData } from '@soccerwatch/common'
import { LivekitContainer } from '../../services/livekitContainer'

import './CalibrationMask.scss'

const DEFAULT_ROW_COUNT = 6
/** N6 keypoint mask: full frame and per-tile resolution (stream may be smaller). */
const CANON_TILE_W = 3840
const CANON_TILE_H = 2160
const EXPORT_W = CANON_TILE_W

/** Number of single-grid streams per hardware platform (N4 = 4, N6 = 6). */
function getCalibrationMaskRowCount(hardwarePlatform?: string): number {
  const p = (hardwarePlatform ?? '').toLowerCase()
  if (p.includes('n4')) return 4
  return DEFAULT_ROW_COUNT
}

const LAYOUT_LEFT_WIDTH_STORAGE_KEY = 'calibrationMaskLeftWidthPx'
const DEFAULT_LEFT_PANEL_PX = 360
const MIN_LEFT_PANEL_PX = 260
const MIN_RIGHT_PANEL_PX = 280
const SPLITTER_LAYOUT_RESERVE_PX = 28

function readStoredLeftPanelWidth(): number {
  try {
    const s = localStorage.getItem(LAYOUT_LEFT_WIDTH_STORAGE_KEY)
    if (s) {
      const n = parseInt(s, 10)
      if (!Number.isNaN(n) && n >= MIN_LEFT_PANEL_PX && n <= 1400) return n
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_LEFT_PANEL_PX
}

type Point = { x: number; y: number }
type MaskPolygon = { id: string; points: Point[] }

export type CalibrationMaskPageProps = RouteComponentProps & {
  cameraId: string
  camera: CameraData
  livekitContainer: LivekitContainer
}

type LeftViewMode = 'grid' | 'mask' | 'overlay'

function isN6CalibrationMaskCamera(hardwarePlatform?: string): boolean {
  const p = (hardwarePlatform ?? '').toLowerCase()
  return (p.includes('n6') || p.includes('n4')) && !p.includes('k2')
}

function makeHatchPattern(): CanvasPattern | null {
  const tile = document.createElement('canvas')
  tile.width = 10
  tile.height = 10
  const tctx = tile.getContext('2d')
  if (!tctx) return null
  tctx.strokeStyle = 'rgba(220, 40, 40, 0.85)'
  tctx.lineWidth = 1.5
  tctx.beginPath()
  tctx.moveTo(0, 10)
  tctx.lineTo(10, 0)
  tctx.stroke()
  tctx.beginPath()
  tctx.moveTo(-2, 2)
  tctx.lineTo(2, -2)
  tctx.stroke()
  tctx.beginPath()
  tctx.moveTo(8, 12)
  tctx.lineTo(12, 8)
  tctx.stroke()
  const p = document.createElement('canvas')
  p.width = 10
  p.height = 10
  const pctx = p.getContext('2d')
  if (!pctx) return null
  return pctx.createPattern(tile, 'repeat')
}

function newPolygonId(): string {
  return `poly-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/** Polygons use tile coordinates 0…CANON_TILE_W × 0…CANON_TILE_H per row. */
function drawExportMask(
  ctx: CanvasRenderingContext2D,
  polygonsByRow: Record<number, MaskPolygon[]>,
  rowCount: number
): void {
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, EXPORT_W, CANON_TILE_H * rowCount)
  ctx.fillStyle = '#ffffff'
  for (let r = 0; r < rowCount; r++) {
    const polys = polygonsByRow[r] ?? []
    const yOff = r * CANON_TILE_H
    for (const poly of polys) {
      if (poly.points.length < 3) continue
      ctx.beginPath()
      const p0 = poly.points[0]
      ctx.moveTo(p0.x, p0.y + yOff)
      for (let i = 1; i < poly.points.length; i++) {
        const p = poly.points[i]
        ctx.lineTo(p.x, p.y + yOff)
      }
      ctx.closePath()
      ctx.fill()
    }
  }
}

const CalibrationMaskPage: React.FC<CalibrationMaskPageProps> = (props) => {
  const { cameraId, camera, history, livekitContainer } = props
  const { connected, element, urlElement } = livekitContainer.state

  const rowCount = getCalibrationMaskRowCount(camera.hardwarePlatform)
  const exportH = CANON_TILE_H * rowCount

  const videoMountRef = useRef<HTMLDivElement>(null)
  const layoutRef = useRef<HTMLDivElement>(null)
  const leftCanvasWrapRef = useRef<HTMLDivElement>(null)
  const rightPanelRef = useRef<HTMLDivElement>(null)
  const splitDragRef = useRef<{ startX: number; startW: number; pointerId: number } | null>(null)
  const leftCanvasRef = useRef<HTMLCanvasElement>(null)
  const rightCanvasRef = useRef<HTMLCanvasElement>(null)
  const exportCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const snapshotImgRef = useRef<HTMLImageElement | null>(null)
  const rafRef = useRef<number>(0)
  const hatchPatternRef = useRef<CanvasPattern | null>(null)
  /** Container widths — updated on window/visualViewport resize + light throttle in drawFrame (no ResizeObserver). */
  const layoutDimsRef = useRef({ left: 720, right: 720 })
  const layoutMeasureThrottleRef = useRef(0)

  const [selectedRow, setSelectedRow] = useState(0)
  const [leftViewMode, setLeftViewMode] = useState<LeftViewMode>('grid')
  const [polygonsByRow, setPolygonsByRow] = useState<Record<number, MaskPolygon[]>>({})
  const [draftPoints, setDraftPoints] = useState<Point[]>([])
  const [mediaSize, setMediaSize] = useState<{ w: number; h: number } | null>(null)
  const [leftPanelWidthPx, setLeftPanelWidthPx] = useState(readStoredLeftPanelWidth)

  const clampLeftPanelWidth = useCallback((w: number, layoutInnerWidth: number) => {
    const maxLeft = layoutInnerWidth - MIN_RIGHT_PANEL_PX - SPLITTER_LAYOUT_RESERVE_PX
    if (maxLeft < MIN_LEFT_PANEL_PX) return MIN_LEFT_PANEL_PX
    return Math.round(Math.max(MIN_LEFT_PANEL_PX, Math.min(maxLeft, w)))
  }, [])

  useEffect(() => {
    setDraftPoints([])
  }, [selectedRow])

  const stateRef = useRef({
    selectedRow,
    leftViewMode,
    polygonsByRow,
    draftPoints
  })
  stateRef.current = { selectedRow, leftViewMode, polygonsByRow, draftPoints }

  useEffect(() => {
    if (!isN6CalibrationMaskCamera(camera.hardwarePlatform)) {
      history.replace(`/cameramonitor/${camera.RowKey}`)
    }
  }, [camera.hardwarePlatform, camera.RowKey, history])

  useEffect(() => {
    livekitContainer.livekitConnectionStart(cameraId)
    return () => {
      livekitContainer.cleaningState()
    }
  }, [cameraId, livekitContainer])

  useEffect(() => {
    const mount = videoMountRef.current
    const el = element as HTMLVideoElement | undefined
    if (el && mount && el.parentNode !== mount) {
      mount.appendChild(el)
    }
  }, [element])

  useEffect(() => {
    if (element || !urlElement) {
      snapshotImgRef.current = null
      return
    }
    const img = new Image()
    img.onload = () => {
      snapshotImgRef.current = img
      setMediaSize({ w: img.naturalWidth, h: img.naturalHeight })
    }
    img.onerror = () => {
      snapshotImgRef.current = null
    }
    img.src = urlElement
  }, [element, urlElement])

  useEffect(() => {
    const v = element as HTMLVideoElement | undefined
    if (!v) return
    const onMeta = () => {
      if (v.videoWidth > 0 && v.videoHeight > 0) {
        setMediaSize({ w: v.videoWidth, h: v.videoHeight })
      }
    }
    v.addEventListener('loadedmetadata', onMeta)
    onMeta()
    return () => v.removeEventListener('loadedmetadata', onMeta)
  }, [element])

  const rebuildExportCanvas = useCallback(
    (polys: Record<number, MaskPolygon[]>) => {
      let c = exportCanvasRef.current
      if (!c || c.width !== EXPORT_W || c.height !== exportH) {
        c = document.createElement('canvas')
        c.width = EXPORT_W
        c.height = exportH
        exportCanvasRef.current = c
      }
      const ctx = c.getContext('2d')
      if (!ctx) return
      drawExportMask(ctx, polys, rowCount)
    },
    [rowCount, exportH]
  )

  useEffect(() => {
    rebuildExportCanvas(polygonsByRow)
  }, [polygonsByRow, rebuildExportCanvas])

  useEffect(() => {
    hatchPatternRef.current = makeHatchPattern()
  }, [])

  useEffect(() => {
    if (!connected || !mediaSize) return

    const measure = () => {
      const layoutEl = layoutRef.current
      if (layoutEl) {
        const lw = layoutEl.getBoundingClientRect().width
        setLeftPanelWidthPx((prev) => clampLeftPanelWidth(prev, lw))
      }
      const leftEl = leftCanvasWrapRef.current
      const rightEl = rightPanelRef.current
      if (!leftEl || !rightEl) return
      layoutDimsRef.current = {
        left: Math.max(1, leftEl.clientWidth),
        right: Math.max(1, rightEl.clientWidth)
      }
    }

    const onResizeOrScroll = () => requestAnimationFrame(measure)

    measure()
    window.addEventListener('resize', onResizeOrScroll)
    window.visualViewport?.addEventListener('resize', onResizeOrScroll)

    return () => {
      window.removeEventListener('resize', onResizeOrScroll)
      window.visualViewport?.removeEventListener('resize', onResizeOrScroll)
    }
  }, [connected, mediaSize, clampLeftPanelWidth])

  const getSource = useCallback((): HTMLVideoElement | HTMLImageElement | null => {
    if (element) {
      const v = element as HTMLVideoElement
      if (v.readyState >= 2 && v.videoWidth > 0) return v
      return null
    }
    return snapshotImgRef.current
  }, [element])

  const drawFrame = useCallback(() => {
    const src = getSource()
    const { w: vw, h: vh } = mediaSize ?? { w: 0, h: 0 }
    if (!src || vw <= 0 || vh <= 0) return

    const leftCv = leftCanvasRef.current
    const rightCv = rightCanvasRef.current
    if (!leftCv || !rightCv) return

    const now = performance.now()
    if (now - layoutMeasureThrottleRef.current > 400) {
      layoutMeasureThrottleRef.current = now
      const le = leftCanvasWrapRef.current
      const re = rightPanelRef.current
      if (le && re) {
        layoutDimsRef.current = {
          left: Math.max(1, le.clientWidth),
          right: Math.max(1, re.clientWidth)
        }
      }
    }

    const leftWrapW = layoutDimsRef.current.left
    const maxDisplayW = Math.max(280, Math.min(leftWrapW || 720, 1600))
    const scale = Math.min(1, maxDisplayW / vw)
    const dispW = Math.round(vw * scale)
    const dispH = Math.round(vh * scale)
    const tileH = vh / rowCount

    if (leftCv.width !== dispW || leftCv.height !== dispH) {
      leftCv.width = dispW
      leftCv.height = dispH
    }

    const rightColW = layoutDimsRef.current.right
    const maxRightW = Math.max(280, Math.min(rightColW || 720, 1200))
    const rightDispW = Math.floor(maxRightW)
    const rightDispH = Math.max(160, Math.round(rightDispW * (CANON_TILE_H / CANON_TILE_W)))
    if (rightCv.width !== rightDispW || rightCv.height !== rightDispH) {
      rightCv.width = rightDispW
      rightCv.height = rightDispH
    }

    const lctx = leftCv.getContext('2d', { willReadFrequently: false })
    const rctx = rightCv.getContext('2d', { willReadFrequently: false })
    if (!lctx || !rctx) return

    const s = stateRef.current
    const exportCanvas = exportCanvasRef.current

    const drawFullSource = (ctx: CanvasRenderingContext2D, dw: number, dh: number) => {
      ctx.drawImage(src as CanvasImageSource, 0, 0, vw, vh, 0, 0, dw, dh)
    }

    if (s.leftViewMode === 'grid') {
      drawFullSource(lctx, dispW, dispH)
    } else if (s.leftViewMode === 'mask' && exportCanvas) {
      lctx.drawImage(exportCanvas, 0, 0, EXPORT_W, exportH, 0, 0, dispW, dispH)
    } else if (s.leftViewMode === 'overlay') {
      drawFullSource(lctx, dispW, dispH)
      if (exportCanvas) {
        lctx.save()
        lctx.globalAlpha = 0.45
        lctx.drawImage(exportCanvas, 0, 0, EXPORT_W, exportH, 0, 0, dispW, dispH)
        lctx.restore()
      }
    } else {
      drawFullSource(lctx, dispW, dispH)
    }

    const rowH = dispH / rowCount
    lctx.strokeStyle = 'rgba(255,255,255,0.35)'
    lctx.lineWidth = 1
    for (let i = 1; i < rowCount; i++) {
      lctx.beginPath()
      lctx.moveTo(0, i * rowH)
      lctx.lineTo(dispW, i * rowH)
      lctx.stroke()
    }
    lctx.fillStyle = 'rgba(0, 180, 255, 0.12)'
    lctx.fillRect(0, s.selectedRow * rowH, dispW, rowH)
    lctx.strokeStyle = 'rgba(0, 200, 255, 0.7)'
    lctx.lineWidth = 2
    lctx.strokeRect(1, s.selectedRow * rowH + 1, dispW - 2, rowH - 2)

    const sy = s.selectedRow * tileH
    rctx.drawImage(src as CanvasImageSource, 0, sy, vw, tileH, 0, 0, rightDispW, rightDispH)

    const polys = s.polygonsByRow[s.selectedRow] ?? []
    const hatch = hatchPatternRef.current
    const sx = rightDispW / CANON_TILE_W
    const syScale = rightDispH / CANON_TILE_H

    const drawPolyPath = (ctx: CanvasRenderingContext2D, points: Point[], close: boolean) => {
      if (points.length === 0) return
      ctx.beginPath()
      ctx.moveTo(points[0].x * sx, points[0].y * syScale)
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x * sx, points[i].y * syScale)
      }
      if (close && points.length >= 3) ctx.closePath()
    }

    for (const poly of polys) {
      if (poly.points.length < 3) continue
      if (hatch) {
        rctx.save()
        drawPolyPath(rctx, poly.points, true)
        rctx.clip()
        rctx.fillStyle = hatch
        rctx.fillRect(0, 0, rightDispW, rightDispH)
        rctx.restore()
      }
      rctx.strokeStyle = 'rgba(200, 30, 30, 0.95)'
      rctx.lineWidth = 2
      drawPolyPath(rctx, poly.points, true)
      rctx.stroke()
    }

    if (s.draftPoints.length > 0) {
      rctx.strokeStyle = 'rgba(255, 120, 120, 0.9)'
      rctx.lineWidth = 2
      rctx.setLineDash([4, 4])
      drawPolyPath(rctx, s.draftPoints, false)
      rctx.stroke()
      rctx.setLineDash([])
      for (const pt of s.draftPoints) {
        rctx.beginPath()
        rctx.arc(pt.x * sx, pt.y * syScale, 4, 0, Math.PI * 2)
        rctx.fillStyle = 'rgba(255,200,100,0.95)'
        rctx.fill()
      }
    }
  }, [getSource, mediaSize, rowCount, exportH])

  useEffect(() => {
    const loop = () => {
      drawFrame()
      rafRef.current = requestAnimationFrame(loop)
    }
    if (connected && mediaSize && mediaSize.w > 0) {
      rafRef.current = requestAnimationFrame(loop)
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [connected, mediaSize, drawFrame])

  const onLeftCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const cv = leftCanvasRef.current
    if (!cv || !mediaSize) return
    const rect = cv.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const scaleX = cv.width / rect.width
    const scaleY = cv.height / rect.height
    const iy = y * scaleY
    const row = Math.min(rowCount - 1, Math.max(0, Math.floor((iy / cv.height) * rowCount)))
    setSelectedRow(row)
  }

  const onRightCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const cv = rightCanvasRef.current
    if (!cv) return
    const rect = cv.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    const scaleX = cv.width / rect.width
    const scaleY = cv.height / rect.height
    const ix = mx * scaleX
    const iy = my * scaleY
    const px = (ix / cv.width) * CANON_TILE_W
    const py = (iy / cv.height) * CANON_TILE_H
    setDraftPoints((prev) => [...prev, { x: px, y: py }])
  }

  const closePolygon = () => {
    setDraftPoints((prev) => {
      if (prev.length < 3) return prev
      const row = stateRef.current.selectedRow
      const poly: MaskPolygon = { id: newPolygonId(), points: [...prev] }
      setPolygonsByRow((pbr) => ({
        ...pbr,
        [row]: [...(pbr[row] ?? []), poly]
      }))
      return []
    })
  }

  const deleteLastPolygon = () => {
    const row = selectedRow
    setPolygonsByRow((pbr) => {
      const list = [...(pbr[row] ?? [])]
      list.pop()
      return { ...pbr, [row]: list }
    })
  }

  const deletePolygonById = (id: string) => {
    const row = selectedRow
    setPolygonsByRow((pbr) => ({
      ...pbr,
      [row]: (pbr[row] ?? []).filter((p) => p.id !== id)
    }))
  }

  const downloadPng = () => {
    rebuildExportCanvas(polygonsByRow)
    const finalC = exportCanvasRef.current
    if (!finalC) return
    finalC.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${cameraId}-keypoint_search_mask.png`
      a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }

  const rowPolygons = polygonsByRow[selectedRow] ?? []

  const onSplitterPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      const layout = layoutRef.current
      if (!layout) return
      const rect = layout.getBoundingClientRect()
      const target = e.currentTarget
      target.setPointerCapture(e.pointerId)
      splitDragRef.current = {
        startX: e.clientX,
        startW: clampLeftPanelWidth(leftPanelWidthPx, rect.width),
        pointerId: e.pointerId
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const onMove = (ev: PointerEvent) => {
        const d = splitDragRef.current
        if (!d || ev.pointerId !== d.pointerId) return
        const r = layoutRef.current?.getBoundingClientRect()
        if (!r) return
        const next = clampLeftPanelWidth(d.startW + (ev.clientX - d.startX), r.width)
        setLeftPanelWidthPx(next)
      }

      const onUp = (ev: PointerEvent) => {
        const d = splitDragRef.current
        if (!d || ev.pointerId !== d.pointerId) return
        splitDragRef.current = null
        try {
          target.releasePointerCapture(ev.pointerId)
        } catch {
          /* ignore */
        }
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        setLeftPanelWidthPx((prev) => {
          try {
            localStorage.setItem(LAYOUT_LEFT_WIDTH_STORAGE_KEY, String(prev))
          } catch {
            /* ignore */
          }
          return prev
        })
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [clampLeftPanelWidth, leftPanelWidthPx]
  )

  const onSplitterKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      e.preventDefault()
      const layout = layoutRef.current
      if (!layout) return
      const rect = layout.getBoundingClientRect()
      const delta = e.key === 'ArrowLeft' ? -24 : 24
      setLeftPanelWidthPx((prev) => {
        const next = clampLeftPanelWidth(prev + delta, rect.width)
        try {
          localStorage.setItem(LAYOUT_LEFT_WIDTH_STORAGE_KEY, String(next))
        } catch {
          /* ignore */
        }
        return next
      })
    },
    [clampLeftPanelWidth]
  )

  return (
    <PageWrapper
      headerTitle={`${i18n.t('calibrationMask.headerTitle')} – ${camera.hardwarePlatform} – ${camera.RowKey}`}
      linkTo={`/cameramonitor/${camera.RowKey}`}
      simpleTopbar
    >
      <div className='calibration-mask-page'>
        <div ref={videoMountRef} className='calibration-mask-hidden-video' aria-hidden />

        {!connected || !mediaSize ? (
          <div className='calibration-mask-loading'>
            <AiswSpinner />
            <div>{i18n.t('calibrationMask.connection')}</div>
          </div>
        ) : null}

        {connected && mediaSize ? (
          <div ref={layoutRef} className='calibration-mask-layout'>
            <div className='calibration-mask-left' style={{ width: leftPanelWidthPx }}>
              <div className='calibration-mask-modes'>
                <div className='calibration-mask-mode-buttons'>
                  <StaigeButton
                    label={i18n.t('calibrationMask.modeGrid')}
                    style={leftViewMode === 'grid' ? 'primary' : 'ghost'}
                    onClick={() => setLeftViewMode('grid')}
                  />
                  <StaigeButton
                    label={i18n.t('calibrationMask.modeMask')}
                    style={leftViewMode === 'mask' ? 'primary' : 'ghost'}
                    onClick={() => setLeftViewMode('mask')}
                  />
                  <StaigeButton
                    label={i18n.t('calibrationMask.modeOverlay')}
                    style={leftViewMode === 'overlay' ? 'primary' : 'ghost'}
                    onClick={() => setLeftViewMode('overlay')}
                  />
                </div>
              </div>
              <p className='calibration-mask-hint'>{i18n.t('calibrationMask.hintClickGrid')}</p>
              <div
                ref={leftCanvasWrapRef}
                className='calibration-mask-canvas-wrap calibration-mask-canvas-wrap--grid'
              >
                <canvas ref={leftCanvasRef} onClick={onLeftCanvasClick} />
              </div>
            </div>

            <div
              className='calibration-mask-splitter'
              role='separator'
              aria-orientation='vertical'
              aria-label={i18n.t('calibrationMask.resizeSplit')}
              tabIndex={0}
              onPointerDown={onSplitterPointerDown}
              onKeyDown={onSplitterKeyDown}
            />

            <div ref={rightPanelRef} className='calibration-mask-right'>
              <div className='calibration-mask-modes-label'>
                {i18n.t('calibrationMask.tilePreview')} ({selectedRow + 1} / {rowCount})
              </div>
              <div className='calibration-mask-canvas-wrap calibration-mask-canvas-wrap--tile'>
                <canvas ref={rightCanvasRef} onClick={onRightCanvasClick} />
              </div>
              <p className='calibration-mask-hint'>{i18n.t('calibrationMask.hintDraw')}</p>

              <div className='calibration-mask-toolbar'>
                <StaigeButton
                  label={i18n.t('calibrationMask.closePolygon')}
                  style='secondary'
                  onClick={closePolygon}
                />
                <StaigeButton
                  label={i18n.t('calibrationMask.deleteLastPolygon')}
                  style='ghost'
                  onClick={deleteLastPolygon}
                />
              </div>

              {rowPolygons.length > 0 ? (
                <div>
                  <div className='calibration-mask-modes-label'>{i18n.t('calibrationMask.polygons')}</div>
                  <div className='calibration-mask-poly-list'>
                    {rowPolygons.map((p, idx) => (
                      <div key={p.id} className='calibration-mask-poly-row'>
                        <span>
                          {i18n.t('calibrationMask.polygonLabel')} {idx + 1} ({p.points.length}{' '}
                          {i18n.t('calibrationMask.points')})
                        </span>
                        <StaigeButton
                          label={i18n.t('calibrationMask.deletePolygon')}
                          style='ghost'
                          size='small'
                          onClick={() => deletePolygonById(p.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <StaigeButton
                label={i18n.t('calibrationMask.downloadPng')}
                style='white'
                onClick={downloadPng}
              />
            </div>
          </div>
        ) : null}
      </div>
    </PageWrapper>
  )
}

export default CalibrationMaskPage
